import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import type {
  WorldType,
  Room,
  PlayerState,
  InputState,
  InventoryItem,
  GameConfig,
  Quest,
  QuestObjective,
  Riddle,
} from '../types';
import type { Vector3Tuple } from 'three';

// ============================================
// GAME STORE STATE
// ============================================

interface GameStoreState {
  // Game config
  config: GameConfig | null;
  isLoaded: boolean;

  // World state
  currentWorld: WorldType;
  isTransitioning: boolean;
  transitionProgress: number;

  // Room state
  currentRoom: Room | null;
  rooms: Map<string, Room>;

  // Player state
  player: PlayerState;
  targetPosition: Vector3Tuple | null; // For tap-to-move

  // Input state
  input: InputState;
  inputMode: 'keyboard' | 'touch';

  // Pending interaction (for walk-to-interact)
  pendingInteraction: string | null; // Object ID to interact with when player arrives

  // UI state
  isPaused: boolean;
  showInventory: boolean;
  showLevelSelect: boolean;
  currentMessage: string | null;

  // Quest state
  currentQuest: Quest | null;
  questComplete: boolean;

  // Progress tracking
  completedRooms: string[];

  // Room progress (quest, doors, hidden objects per room)
  roomProgress: Record<string, {
    questProgress?: { objectives: { id: string; completed: boolean }[]; isComplete: boolean };
    openedDoors: string[];
    hiddenObjects: string[];
  }>;

  // Door state (current room)
  openedDoors: Set<string>;

  // Hidden objects (picked up items, etc.) - current room
  hiddenObjects: Set<string>;

  // Show title screen
  showTitleScreen: boolean;

  // Visual effects
  visualEffect: string | null;

  // Riddle state
  currentRiddle: Riddle | null;
  solvedRiddles: Set<string>;

  // Debug mode
  debugMode: boolean;

  // Camera/Zoom
  cameraZoom: number;

  // UI Preferences
  showJoystick: boolean;
  showZoomControls: boolean;

  // Actions
  loadConfig: (config: GameConfig) => void;
  switchWorld: () => void;
  setTransitionProgress: (progress: number) => void;
  loadRoom: (roomId: string) => void;
  movePlayer: (position: Vector3Tuple) => void;
  setTargetPosition: (position: Vector3Tuple | null) => void;
  addToInventory: (item: InventoryItem) => void;
  removeFromInventory: (itemId: string) => void;
  solvePuzzle: (puzzleId: string) => void;
  // Bike actions
  mountBike: (color: 'blue' | 'yellow' | 'red') => void;
  dismountBike: () => void;
  updateInput: (input: Partial<InputState>) => void;
  setInputMode: (mode: 'keyboard' | 'touch') => void;
  setPendingInteraction: (objectId: string | null) => void;
  togglePause: () => void;
  toggleInventory: () => void;
  showMessage: (message: string) => void;
  clearMessage: () => void;
  resetGame: () => void;

  // Quest actions
  setQuest: (quest: Quest) => void;
  completeObjective: (objectiveId: string) => void;
  interactWithObject: (objectId: string) => void;
  restartQuest: () => void;
  advanceToNextRoom: () => void;

  // Level select actions
  toggleLevelSelect: () => void;
  goToRoom: (roomId: string) => void;
  saveProgress: () => void;
  loadProgress: () => void;
  startNewGame: () => void;
  continueGame: () => void;
  hideTitleScreen: () => void;
  hasSavedProgress: () => boolean;

  // Door actions
  openDoor: (doorId: string) => void;
  isDoorOpen: (doorId: string) => boolean;
  openDoorWithItem: (doorId: string, requiredItemId: string) => boolean;

  // Object visibility actions
  hideObject: (objectId: string) => void;
  isObjectHidden: (objectId: string) => boolean;

  // Inventory helpers
  hasInventoryItem: (itemId: string) => boolean;
  resetInventory: () => void;
  giveItemToNPC: (itemId: string, npcId: string) => boolean;

  // Visual effects actions
  setVisualEffect: (effect: string | null) => void;

  // Riddle actions
  showRiddle: (riddle: Riddle) => void;
  answerRiddle: (answer: string) => boolean;
  closeRiddle: () => void;
  isRiddleSolved: (riddleId: string) => boolean;
  solveRiddle: (riddleId: string) => void;

  // Debug actions
  toggleDebugMode: () => void;

  // Camera/Zoom actions
  setZoom: (zoom: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;

  // UI Preferences actions
  toggleJoystick: () => void;
  toggleZoomControls: () => void;
}

// ============================================
// INITIAL STATE
// ============================================

const initialPlayerState: PlayerState = {
  currentRoomId: '',
  position: [0, 0, 0],
  inventory: [],
  solvedPuzzles: [],
  visitedRooms: [],
  onBike: false,
  currentBikeColor: null,
};

const initialInputState: InputState = {
  movement: { x: 0, y: 0 },
  interact: false,
  switchWorld: false,
  pause: false,
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Find a safe spawn position in a room, avoiding triggers and interactive objects
 */
function findSafeSpawnPosition(room: Room): [number, number, number] {
  // Use room's spawnPosition if defined
  if (room.spawnPosition) {
    return room.spawnPosition as [number, number, number];
  }

  const defaultPos: [number, number, number] = [room.size[0] / 2, 0, room.size[2] / 2];

  // Check if default position is too close to a trigger
  const isTooCloseToObject = (pos: [number, number, number]) => {
    return room.objects.some((obj) => {
      // Check triggers (pressure plates, etc.)
      if (obj.type === 'trigger') {
        const dx = obj.position[0] - pos[0];
        const dz = obj.position[2] - pos[2];
        const dist = Math.sqrt(dx * dx + dz * dz);
        const triggerRadius = Math.max(obj.scale[0], obj.scale[2]) * 0.6 + 0.5;
        return dist < triggerRadius;
      }
      return false;
    });
  };

  if (!isTooCloseToObject(defaultPos)) {
    return defaultPos;
  }

  // Try alternative spawn positions
  const alternatives: [number, number, number][] = [
    [room.size[0] / 4, 0, room.size[2] / 4],
    [room.size[0] * 3 / 4, 0, room.size[2] / 4],
    [room.size[0] / 4, 0, room.size[2] * 3 / 4],
    [room.size[0] * 3 / 4, 0, room.size[2] * 3 / 4],
    [room.size[0] / 2, 0, room.size[2] / 4],
    [room.size[0] / 2, 0, room.size[2] * 3 / 4],
    [room.size[0] / 4, 0, room.size[2] / 2],
    [room.size[0] * 3 / 4, 0, room.size[2] / 2],
  ];

  for (const alt of alternatives) {
    if (!isTooCloseToObject(alt)) {
      return alt;
    }
  }

  // Fallback to default if all alternatives fail
  return defaultPos;
}

// ============================================
// GAME STORE
// ============================================

export const useGameStore = create<GameStoreState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    config: null,
    isLoaded: false,
    currentWorld: 'real',
    isTransitioning: false,
    transitionProgress: 0,
    currentRoom: null,
    rooms: new Map(),
    player: initialPlayerState,
    targetPosition: null,
    input: initialInputState,
    inputMode: 'keyboard',
    pendingInteraction: null,
    isPaused: false,
    showInventory: false,
    showLevelSelect: false,
    currentMessage: null,
    currentQuest: null,
    questComplete: false,
    completedRooms: [],
    roomProgress: {},
    openedDoors: new Set<string>(),
    hiddenObjects: new Set<string>(),
    showTitleScreen: true,
    visualEffect: null,
    currentRiddle: null,
    solvedRiddles: new Set<string>(),
    debugMode: false,
    cameraZoom: 50, // Default zoom level
    showJoystick: true,
    showZoomControls: true,

    // Actions
    loadConfig: (config) => {
      const roomsMap = new Map<string, Room>();
      config.rooms.forEach((room) => roomsMap.set(room.id, room));

      const startingRoom = roomsMap.get(config.startingRoomId);

      // Find initial quest for starting room
      const initialQuest = config.quests?.find((q) => q.roomId === config.startingRoomId) || null;

      // Find safe spawn position
      const spawnPosition = startingRoom
        ? findSafeSpawnPosition(startingRoom)
        : [5, 0, 5] as [number, number, number];

      set({
        config,
        rooms: roomsMap,
        currentRoom: startingRoom || null,
        player: {
          ...initialPlayerState,
          currentRoomId: config.startingRoomId,
          position: spawnPosition,
        },
        currentQuest: initialQuest,
        questComplete: false,
        isLoaded: true,
        openedDoors: new Set<string>(), // Reset doors on config load
        hiddenObjects: new Set<string>(), // Reset hidden objects on config load
      });
    },

    switchWorld: () => {
      const { isTransitioning, currentWorld, currentRoom, currentQuest, completeObjective, showMessage, solvedRiddles } = get();

      if (isTransitioning) return;
      if (!currentRoom?.hasSpectralLayer) return;

      // In Hawkins, require Vekeuna to be helped before allowing free world switch
      // The rift is the only way to travel between worlds until then
      if (currentRoom?.id === 'room_hawkins_reveil' && !solvedRiddles.has('vekeuna_helped')) {
        showMessage('Tu n\'es pas encore assez forte pour changer de dimension par toi-même... Utilise la faille !');
        return;
      }

      set({ isTransitioning: true, transitionProgress: 0 });

      // The actual world switch happens when transition completes
      const targetWorld = currentWorld === 'real' ? 'spectral' : 'real';

      // Simulate transition (this would be animated in the component)
      setTimeout(() => {
        set({
          currentWorld: targetWorld,
          isTransitioning: false,
          transitionProgress: 1,
        });

        // Check for world-switch objectives (objectives that only require being in a world)
        if (currentQuest) {
          const worldSwitchObjective = currentQuest.objectives.find(
            (obj) => !obj.completed && obj.requiredWorld === targetWorld && obj.targetObjectId?.startsWith('world_switch_')
          );
          if (worldSwitchObjective) {
            completeObjective(worldSwitchObjective.id);
            showMessage(`✓ ${worldSwitchObjective.description}`);
          }
        }
      }, 800);
    },

    setTransitionProgress: (progress) => {
      set({ transitionProgress: progress });
    },

    loadRoom: (roomId) => {
      const { rooms, player, config, currentRoom, currentQuest, questComplete, openedDoors, hiddenObjects, roomProgress, completedRooms } = get();
      const room = rooms.get(roomId);

      if (!room) {
        console.error(`Room ${roomId} not found`);
        return;
      }

      const visitedRooms = player.visitedRooms.includes(roomId)
        ? player.visitedRooms
        : [...player.visitedRooms, roomId];

      // Save current room's progress before switching
      const newRoomProgress = { ...roomProgress };
      let newCompletedRooms = [...completedRooms];

      if (currentRoom) {
        // Save quest progress, doors, hidden objects
        newRoomProgress[currentRoom.id] = {
          questProgress: currentQuest ? {
            objectives: currentQuest.objectives.map(o => ({ id: o.id, completed: o.completed })),
            isComplete: currentQuest.isComplete,
            subQuests: currentQuest.subQuests?.map(sq => ({
              id: sq.id,
              isComplete: sq.isComplete,
              objectives: sq.objectives.map(o => ({ id: o.id, completed: o.completed })),
            })),
          } : undefined,
          openedDoors: Array.from(openedDoors),
          hiddenObjects: Array.from(hiddenObjects),
        };

        // Mark current room as completed if quest is complete
        if ((questComplete || currentQuest?.isComplete) && !newCompletedRooms.includes(currentRoom.id)) {
          newCompletedRooms = [...newCompletedRooms, currentRoom.id];
        }
      }

      // Find the quest for target room
      const roomQuest = config?.quests?.find((q) => q.roomId === roomId) || null;

      // Check if we have saved progress for the target room
      const savedProgress = newRoomProgress[roomId];

      // Restore quest with saved progress if available
      let restoredQuest = null;
      if (roomQuest) {
        if (savedProgress?.questProgress) {
          restoredQuest = {
            ...roomQuest,
            objectives: roomQuest.objectives.map(o => {
              const saved = savedProgress.questProgress?.objectives.find((so: { id: string }) => so.id === o.id);
              return { ...o, completed: saved?.completed || false };
            }),
            isComplete: savedProgress.questProgress.isComplete,
            subQuests: roomQuest.subQuests?.map(sq => {
              const savedSubQuest = savedProgress.questProgress?.subQuests?.find(
                (ssq: { id: string }) => ssq.id === sq.id
              );
              if (savedSubQuest) {
                return {
                  ...sq,
                  isComplete: savedSubQuest.isComplete,
                  objectives: sq.objectives.map(o => {
                    const savedObj = savedSubQuest.objectives.find(
                      (so: { id: string }) => so.id === o.id
                    );
                    return { ...o, completed: savedObj?.completed || false };
                  }),
                };
              }
              return { ...sq, objectives: sq.objectives.map(o => ({ ...o, completed: false })) };
            }),
          };
        } else {
          // New room, initialize quest
          restoredQuest = {
            ...roomQuest,
            objectives: roomQuest.objectives.map(o => ({ ...o, completed: false })),
            isComplete: false,
            subQuests: roomQuest.subQuests?.map(sq => ({
              ...sq,
              isComplete: false,
              objectives: sq.objectives.map(o => ({ ...o, completed: false })),
            })),
          };
        }
      }

      // Get spawn position for the new room
      const spawnPosition = findSafeSpawnPosition(room);

      set({
        currentRoom: room,
        currentWorld: room.initialWorld,
        targetPosition: null,
        player: {
          ...player,
          currentRoomId: roomId,
          visitedRooms,
          position: spawnPosition,
        },
        currentQuest: restoredQuest,
        questComplete: restoredQuest?.isComplete || false,
        roomProgress: newRoomProgress,
        completedRooms: newCompletedRooms,
        // Restore doors and hidden objects from saved progress
        openedDoors: new Set<string>(savedProgress?.openedDoors || []),
        hiddenObjects: new Set<string>(savedProgress?.hiddenObjects || []),
      });

      // Save progress after room switch
      setTimeout(() => get().saveProgress(), 100);
    },

    movePlayer: (position) => {
      set((state) => ({
        player: { ...state.player, position },
      }));
    },

    setTargetPosition: (position) => {
      set({ targetPosition: position });
    },

    addToInventory: (item) => {
      const { player } = get();
      // Prevent adding duplicate items
      if (player.inventory.some((i) => i.id === item.id)) {
        console.log(`Item ${item.id} already in inventory, skipping`);
        return;
      }
      set((state) => ({
        player: {
          ...state.player,
          inventory: [...state.player.inventory, item],
        },
      }));
      // Auto-save after collecting an item
      setTimeout(() => get().saveProgress(), 100);
    },

    removeFromInventory: (itemId) => {
      set((state) => ({
        player: {
          ...state.player,
          inventory: state.player.inventory.filter((i) => i.id !== itemId),
        },
      }));
    },

    mountBike: (color) => {
      const { player, showMessage } = get();
      // Can't mount if already on a bike
      if (player.onBike) {
        showMessage('Tu es déjà sur un vélo !');
        return;
      }
      set((state) => ({
        player: {
          ...state.player,
          onBike: true,
          currentBikeColor: color,
        },
      }));
      showMessage(`Tu montes sur le vélo ${color === 'blue' ? 'bleu' : color === 'yellow' ? 'jaune' : 'rouge'} !`);
    },

    dismountBike: () => {
      const { player } = get();
      if (!player.onBike) return;

      const bikeColor = player.currentBikeColor;
      set((state) => ({
        player: {
          ...state.player,
          onBike: false,
          currentBikeColor: null,
        },
      }));
      return bikeColor; // Return the color so we know which bike was deposited
    },

    solvePuzzle: (puzzleId) => {
      const { player, currentRoom, rooms } = get();

      if (player.solvedPuzzles.includes(puzzleId)) return;

      // Update player state
      const newSolvedPuzzles = [...player.solvedPuzzles, puzzleId];

      // Update puzzle state in current room
      if (currentRoom) {
        const updatedRoom = {
          ...currentRoom,
          puzzles: currentRoom.puzzles.map((p) =>
            p.id === puzzleId ? { ...p, state: 'solved' as const } : p
          ),
        };

        const newRooms = new Map(rooms);
        newRooms.set(currentRoom.id, updatedRoom);

        set({
          player: { ...player, solvedPuzzles: newSolvedPuzzles },
          currentRoom: updatedRoom,
          rooms: newRooms,
        });
      } else {
        set({
          player: { ...player, solvedPuzzles: newSolvedPuzzles },
        });
      }
    },

    updateInput: (inputUpdate) => {
      set((state) => ({
        input: { ...state.input, ...inputUpdate },
      }));
    },

    setInputMode: (mode) => {
      set({ inputMode: mode });
    },

    setPendingInteraction: (objectId) => {
      set({ pendingInteraction: objectId });
    },

    togglePause: () => {
      set((state) => ({ isPaused: !state.isPaused }));
    },

    toggleInventory: () => {
      set((state) => ({ showInventory: !state.showInventory }));
    },

    showMessage: (message) => {
      set({ currentMessage: message });
    },

    clearMessage: () => {
      set({ currentMessage: null });
    },

    resetGame: () => {
      const { config } = get();
      if (config) {
        get().loadConfig(config);
      }
      set({
        currentWorld: 'real',
        isPaused: false,
        showInventory: false,
        currentMessage: null,
        currentQuest: null,
        questComplete: false,
      });
    },

    // Quest actions
    setQuest: (quest) => {
      set({ currentQuest: quest, questComplete: false });
    },

    completeObjective: (objectiveId) => {
      const { currentQuest, showMessage } = get();
      if (!currentQuest) return;

      // Check main objectives
      let updatedObjectives = currentQuest.objectives;
      let foundInMain = false;

      if (currentQuest.objectives.some((obj) => obj.id === objectiveId)) {
        updatedObjectives = currentQuest.objectives.map((obj) =>
          obj.id === objectiveId ? { ...obj, completed: true } : obj
        );
        foundInMain = true;
      }

      // Check sub-quest objectives
      let updatedSubQuests = currentQuest.subQuests;
      if (!foundInMain && currentQuest.subQuests) {
        updatedSubQuests = currentQuest.subQuests.map((subQuest) => {
          const hasObjective = subQuest.objectives.some((obj) => obj.id === objectiveId);
          if (hasObjective) {
            const updatedSubObjectives = subQuest.objectives.map((obj) =>
              obj.id === objectiveId ? { ...obj, completed: true } : obj
            );
            const subQuestComplete = updatedSubObjectives.every((obj) => obj.completed);
            return {
              ...subQuest,
              objectives: updatedSubObjectives,
              isComplete: subQuestComplete,
            };
          }
          return subQuest;
        });
      }

      // Main quest is complete when ALL main objectives are done
      // This includes hidden objectives (those with showAfterRiddleSolved that haven't been unlocked yet)
      // A hidden objective still blocks quest completion until it's revealed AND completed
      // (optional sub-quests don't block completion)
      const mainComplete = updatedObjectives.every((obj) => obj.completed);

      set({
        currentQuest: {
          ...currentQuest,
          objectives: updatedObjectives,
          subQuests: updatedSubQuests,
          isComplete: mainComplete,
        },
        questComplete: mainComplete,
      });

      // Auto-save after completing an objective
      setTimeout(() => get().saveProgress(), 100);

      if (mainComplete && currentQuest.reward) {
        setTimeout(() => {
          showMessage(currentQuest.reward!);
        }, 500);
      }
    },

    interactWithObject: (objectId) => {
      const { currentQuest, currentWorld, completeObjective, showMessage, solvedRiddles } = get();
      if (!currentQuest) return;

      // Helper to check if an objective is visible (not hidden behind unsolved riddle)
      const isObjectiveVisible = (obj: QuestObjective) => {
        if (!obj.showAfterRiddleSolved) return true;
        return solvedRiddles.has(obj.showAfterRiddleSolved);
      };

      // Helper to check if objective can be completed by interaction
      // (skip objectives that require a specific riddle to be solved)
      const canCompleteByInteraction = (obj: QuestObjective) => {
        return !obj.completeOnRiddleSolved;
      };

      // Check main quest objectives (only visible ones that can be completed by interaction)
      const mainObjective = currentQuest.objectives.find(
        (obj) => obj.targetObjectId === objectId && !obj.completed && isObjectiveVisible(obj) && canCompleteByInteraction(obj)
      );

      if (mainObjective) {
        const worldMatches = !mainObjective.requiredWorld || mainObjective.requiredWorld === currentWorld;
        if (worldMatches) {
          completeObjective(mainObjective.id);
          showMessage(`✓ ${mainObjective.description}`);
          return;
        } else {
          showMessage(`Cet objet semble réagir... mais pas dans ce monde.`);
          return;
        }
      }

      // Check sub-quest objectives
      if (currentQuest.subQuests) {
        for (const subQuest of currentQuest.subQuests) {
          const subObjective = subQuest.objectives.find(
            (obj) => obj.targetObjectId === objectId && !obj.completed && canCompleteByInteraction(obj)
          );

          if (subObjective) {
            const worldMatches = !subObjective.requiredWorld || subObjective.requiredWorld === currentWorld;
            if (worldMatches) {
              completeObjective(subObjective.id);
              showMessage(`🐾 ${subObjective.description}`);

              // Check if sub-quest is now complete
              const updatedSubQuest = {
                ...subQuest,
                objectives: subQuest.objectives.map((obj) =>
                  obj.id === subObjective.id ? { ...obj, completed: true } : obj
                ),
              };
              if (updatedSubQuest.objectives.every((obj) => obj.completed) && subQuest.reward) {
                setTimeout(() => showMessage(`🎉 ${subQuest.name} terminée !`), 1500);
              }
              return;
            }
          }
        }
      }
    },

    restartQuest: () => {
      const { currentQuest, currentRoom, solvedRiddles, player } = get();
      if (!currentQuest) return;

      // Reset quest objectives and sub-quests
      const resetQuest = {
        ...currentQuest,
        objectives: currentQuest.objectives.map((obj) => ({
          ...obj,
          completed: false,
        })),
        subQuests: currentQuest.subQuests?.map((subQuest) => ({
          ...subQuest,
          objectives: subQuest.objectives.map((obj) => ({
            ...obj,
            completed: false,
          })),
          isComplete: false,
        })),
        isComplete: false,
      };

      // Find safe spawn position
      const position = currentRoom
        ? findSafeSpawnPosition(currentRoom)
        : [5, 0, 5] as [number, number, number];

      // Get room-specific riddle prefixes to clear
      // For Hawkins: dustin_, erica_, bike_, vekeuna_, rift_
      // For Wonderland: hatter_, tea_, card_
      // For sanctuary: oracle_
      const roomRiddlePrefixes: Record<string, string[]> = {
        'room_hawkins_reveil': ['dustin_', 'erica_', 'bike_', 'vekeuna_', 'rift_', 'walkie_', 'demagordon_'],
        'room_tea_party': ['hatter_', 'tea_', 'card_', 'tile_'],
        'room_sanctuary': ['oracle_'],
      };

      const prefixesToClear = currentRoom ? roomRiddlePrefixes[currentRoom.id] || [] : [];

      // Filter out room-specific riddles
      const filteredRiddles = new Set<string>();
      solvedRiddles.forEach(riddleId => {
        const shouldKeep = !prefixesToClear.some(prefix => riddleId.startsWith(prefix));
        if (shouldKeep) {
          filteredRiddles.add(riddleId);
        }
      });

      // Remove bike items from inventory
      const filteredInventory = player.inventory.filter(
        item => !item.id.startsWith('bike_')
      );

      set({
        currentQuest: resetQuest,
        questComplete: false,
        currentWorld: currentRoom?.initialWorld || 'real',
        player: {
          ...get().player,
          position,
          inventory: filteredInventory,
          onBike: false,
          currentBikeColor: null,
        },
        targetPosition: null,
        openedDoors: new Set<string>(), // Reset all opened doors
        hiddenObjects: new Set<string>(), // Reset hidden objects
        solvedRiddles: filteredRiddles, // Reset room-specific riddles only
      });

      // Save progress after restart so page refresh preserves the reset state
      setTimeout(() => get().saveProgress(), 100);
    },

    advanceToNextRoom: () => {
      const { rooms, currentRoom, config, completedRooms, saveProgress } = get();
      if (!currentRoom || !config) return;

      // Mark current room as completed
      if (!completedRooms.includes(currentRoom.id)) {
        set({ completedRooms: [...completedRooms, currentRoom.id] });
      }

      const roomIds = Array.from(rooms.keys());
      const currentIndex = roomIds.indexOf(currentRoom.id);

      if (currentIndex >= 0 && currentIndex < roomIds.length - 1) {
        const nextRoomId = roomIds[currentIndex + 1];
        const nextRoom = rooms.get(nextRoomId);

        if (nextRoom) {
          // Find quest for next room
          const nextQuest = config.quests?.find((q) => q.roomId === nextRoomId);

          set({
            currentRoom: nextRoom,
            currentWorld: nextRoom.initialWorld,
            player: {
              ...get().player,
              currentRoomId: nextRoomId,
              position: findSafeSpawnPosition(nextRoom),
            },
            targetPosition: null,
            currentQuest: nextQuest || null,
            questComplete: false,
            openedDoors: new Set<string>(), // Reset doors when entering new room
            hiddenObjects: new Set<string>(), // Reset hidden objects
          });

          // Save progress after room change
          setTimeout(() => get().saveProgress(), 100);
        }
      }
    },

    // Level select actions
    toggleLevelSelect: () => {
      set((state) => ({ showLevelSelect: !state.showLevelSelect }));
    },

    goToRoom: (roomId) => {
      // goToRoom is called from LevelSelect which already checks unlock conditions
      // Close level select and use loadRoom for the actual room switch
      set({ showLevelSelect: false });
      get().loadRoom(roomId);
    },

    saveProgress: () => {
      const { completedRooms, currentRoom, currentWorld, player, currentQuest, openedDoors, hiddenObjects, roomProgress } = get();

      // Update roomProgress with current room's state
      const updatedRoomProgress = { ...roomProgress };
      if (currentRoom) {
        updatedRoomProgress[currentRoom.id] = {
          questProgress: currentQuest ? {
            objectives: currentQuest.objectives.map(o => ({ id: o.id, completed: o.completed })),
            isComplete: currentQuest.isComplete,
            // Save sub-quest progress
            subQuests: currentQuest.subQuests?.map(sq => ({
              id: sq.id,
              isComplete: sq.isComplete,
              objectives: sq.objectives.map(o => ({ id: o.id, completed: o.completed })),
            })),
          } : undefined,
          openedDoors: Array.from(openedDoors),
          hiddenObjects: Array.from(hiddenObjects),
        };
      }

      const progress = {
        version: 3, // Save format version - updated for room progress
        completedRooms,
        lastRoomId: currentRoom?.id || '',
        lastWorld: currentWorld,
        playerPosition: player.position,
        inventory: player.inventory,
        // Save all room progress
        roomProgress: updatedRoomProgress,
        // Legacy quest progress for current room (backwards compatibility)
        questProgress: currentQuest ? {
          questId: currentQuest.id,
          objectives: currentQuest.objectives.map(o => ({ id: o.id, completed: o.completed })),
          isComplete: currentQuest.isComplete,
          // Include sub-quest progress
          subQuests: currentQuest.subQuests?.map(sq => ({
            id: sq.id,
            isComplete: sq.isComplete,
            objectives: sq.objectives.map(o => ({ id: o.id, completed: o.completed })),
          })),
        } : null,
        // Current room's doors and objects
        openedDoors: Array.from(openedDoors),
        hiddenObjects: Array.from(hiddenObjects),
        solvedRiddles: Array.from(get().solvedRiddles),
        savedAt: Date.now(),
      };
      try {
        localStorage.setItem('hollowveil_progress', JSON.stringify(progress));
        console.log('Game saved successfully');
      } catch (e) {
        console.warn('Could not save progress:', e);
      }
    },

    loadProgress: () => {
      try {
        const saved = localStorage.getItem('hollowveil_progress');
        if (saved) {
          const progress = JSON.parse(saved);

          // Handle version 1 saves (old format)
          if (!progress.version || progress.version < 2) {
            set({ completedRooms: progress.completedRooms || [] });
            if (progress.lastRoomId) {
              const { rooms, config } = get();
              const room = rooms.get(progress.lastRoomId);
              if (room) {
                const quest = config?.quests?.find((q) => q.roomId === progress.lastRoomId);
                set({
                  currentRoom: room,
                  currentWorld: room.initialWorld,
                  player: {
                    ...get().player,
                    currentRoomId: progress.lastRoomId,
                    position: [room.size[0] / 2, 0, room.size[2] / 2],
                  },
                  currentQuest: quest ? { ...quest, objectives: quest.objectives.map((o) => ({ ...o, completed: false })), isComplete: false } : null,
                  showTitleScreen: false,
                });
              }
            }
            return;
          }

          // Version 2+ saves
          const { rooms, config } = get();
          const room = rooms.get(progress.lastRoomId);

          if (room) {
            // Find and restore quest with saved progress
            let restoredQuest = null;
            if (progress.questProgress && config?.quests) {
              const quest = config.quests.find((q) => q.id === progress.questProgress.questId);
              if (quest) {
                restoredQuest = {
                  ...quest,
                  objectives: quest.objectives.map(o => {
                    const savedObj = progress.questProgress.objectives.find((so: { id: string; completed: boolean }) => so.id === o.id);
                    return { ...o, completed: savedObj?.completed || false };
                  }),
                  isComplete: progress.questProgress.isComplete,
                  // Restore sub-quest progress
                  subQuests: quest.subQuests?.map(sq => {
                    const savedSubQuest = progress.questProgress.subQuests?.find(
                      (ssq: { id: string }) => ssq.id === sq.id
                    );
                    if (savedSubQuest) {
                      return {
                        ...sq,
                        isComplete: savedSubQuest.isComplete,
                        objectives: sq.objectives.map(o => {
                          const savedObj = savedSubQuest.objectives.find(
                            (so: { id: string }) => so.id === o.id
                          );
                          return { ...o, completed: savedObj?.completed || false };
                        }),
                      };
                    }
                    return sq;
                  }),
                };
              }
            }

            // Version 3+ saves include roomProgress
            const loadedRoomProgress = progress.version >= 3 ? (progress.roomProgress || {}) : {};

            set({
              completedRooms: progress.completedRooms || [],
              currentRoom: room,
              currentWorld: progress.lastWorld || room.initialWorld,
              player: {
                ...get().player,
                currentRoomId: progress.lastRoomId,
                position: progress.playerPosition || [room.size[0] / 2, 0, room.size[2] / 2],
                inventory: progress.inventory || [],
              },
              currentQuest: restoredQuest,
              roomProgress: loadedRoomProgress,
              openedDoors: new Set(progress.openedDoors || []),
              hiddenObjects: new Set(progress.hiddenObjects || []),
              solvedRiddles: new Set(progress.solvedRiddles || []),
              showTitleScreen: false,
            });

            console.log('Game loaded successfully');
          }
        }
      } catch (e) {
        console.warn('Could not load progress:', e);
      }
    },

    startNewGame: () => {
      const { config } = get();

      // Clear saved progress
      try {
        localStorage.removeItem('hollowveil_progress');
      } catch (e) {
        console.warn('Could not clear saved progress:', e);
      }

      // Reset to initial state
      if (config) {
        const roomsMap = new Map<string, Room>();
        config.rooms.forEach((room) => roomsMap.set(room.id, room));
        const startingRoom = roomsMap.get(config.startingRoomId);
        const initialQuest = config.quests?.find((q) => q.roomId === config.startingRoomId) || null;
        const spawnPosition = startingRoom
          ? findSafeSpawnPosition(startingRoom)
          : [5, 0, 5] as [number, number, number];

        set({
          rooms: roomsMap,
          currentRoom: startingRoom || null,
          currentWorld: startingRoom?.initialWorld || 'real',
          player: {
            ...initialPlayerState,
            currentRoomId: config.startingRoomId,
            position: spawnPosition,
          },
          currentQuest: initialQuest ? {
            ...initialQuest,
            objectives: initialQuest.objectives.map(o => ({ ...o, completed: false })),
            isComplete: false,
          } : null,
          questComplete: false,
          completedRooms: [],
          roomProgress: {},
          openedDoors: new Set<string>(),
          hiddenObjects: new Set<string>(),
          solvedRiddles: new Set<string>(),
          showTitleScreen: false,
          targetPosition: null,
          currentMessage: null,
        });
      }
    },

    continueGame: () => {
      get().loadProgress();
      set({ showTitleScreen: false });
    },

    hideTitleScreen: () => {
      set({ showTitleScreen: false });
    },

    hasSavedProgress: () => {
      try {
        const saved = localStorage.getItem('hollowveil_progress');
        return saved !== null;
      } catch {
        return false;
      }
    },

    // Door actions
    openDoor: (doorId) => {
      const { openedDoors, showMessage } = get();
      if (!openedDoors.has(doorId)) {
        const newOpenedDoors = new Set(openedDoors);
        newOpenedDoors.add(doorId);
        set({ openedDoors: newOpenedDoors });
        showMessage('La porte s\'ouvre lentement...');
        // Auto-save after opening a door
        setTimeout(() => get().saveProgress(), 100);
      }
    },

    isDoorOpen: (doorId) => {
      return get().openedDoors.has(doorId);
    },

    openDoorWithItem: (doorId, requiredItemId) => {
      const { player, openedDoors, showMessage, openDoor } = get();
      const hasItem = player.inventory.some((item) => item.id === requiredItemId);

      if (hasItem) {
        openDoor(doorId);
        return true;
      } else {
        showMessage('Cette porte nécessite un objet spécial pour s\'ouvrir...');
        return false;
      }
    },

    // Object visibility actions
    hideObject: (objectId) => {
      const { hiddenObjects } = get();
      if (!hiddenObjects.has(objectId)) {
        const newHiddenObjects = new Set(hiddenObjects);
        newHiddenObjects.add(objectId);
        set({ hiddenObjects: newHiddenObjects });
        // Auto-save after hiding an object (collecting it)
        setTimeout(() => get().saveProgress(), 100);
      }
    },

    isObjectHidden: (objectId) => {
      return get().hiddenObjects.has(objectId);
    },

    // Inventory helpers
    hasInventoryItem: (itemId) => {
      return get().player.inventory.some((item) => item.id === itemId);
    },

    resetInventory: () => {
      set((state) => ({
        player: {
          ...state.player,
          inventory: [],
        },
      }));
      get().showMessage('Inventaire réinitialisé');
      setTimeout(() => get().saveProgress(), 100);
    },

    giveItemToNPC: (itemId, npcId) => {
      const { player, removeFromInventory, interactWithObject, showMessage } = get();
      const hasItem = player.inventory.some((item) => item.id === itemId);

      if (hasItem) {
        removeFromInventory(itemId);
        interactWithObject(npcId);
        setTimeout(() => get().saveProgress(), 100);
        return true;
      } else {
        showMessage('Vous n\'avez pas l\'objet requis...');
        return false;
      }
    },

    // Visual effects
    setVisualEffect: (effect) => {
      set({ visualEffect: effect });
    },

    // Riddle actions
    showRiddle: (riddle) => {
      // Check if already solved
      if (get().solvedRiddles.has(riddle.id)) {
        // For dialogues (where all choices are correct), always show the response
        // For puzzles, show the success message
        const isDialogue = riddle.type === 'multiple_choice' &&
          riddle.choices?.every(c => c.isCorrect);

        if (isDialogue) {
          // Show the response directly for dialogues
          get().showMessage(riddle.correctResponse);
        } else {
          get().showMessage(riddle.correctResponse);
        }
        return;
      }
      set({ currentRiddle: riddle });
    },

    answerRiddle: (answer) => {
      const { currentRiddle, solvedRiddles, showMessage, interactWithObject, openDoor, addToInventory, removeFromInventory, hideObject } = get();
      if (!currentRiddle) return false;

      let isCorrect = false;

      if (currentRiddle.type === 'multiple_choice' && currentRiddle.choices) {
        // Check if the selected choice is correct
        const selectedChoice = currentRiddle.choices.find((c) => c.id === answer);
        isCorrect = selectedChoice?.isCorrect || false;
      } else if (currentRiddle.type === 'text_input' && currentRiddle.expectedAnswers) {
        // Check if the answer matches any expected answer (case insensitive)
        const normalizedAnswer = answer.toLowerCase().trim();
        isCorrect = currentRiddle.expectedAnswers.some(
          (expected) => expected.toLowerCase().trim() === normalizedAnswer
        );
      }

      if (isCorrect) {
        // Mark as solved
        const newSolvedRiddles = new Set(solvedRiddles);
        newSolvedRiddles.add(currentRiddle.id);
        set({ solvedRiddles: newSolvedRiddles, currentRiddle: null });

        // Show success message
        showMessage(currentRiddle.correctResponse);

        // Execute onCorrect actions
        if (currentRiddle.onCorrect) {
          currentRiddle.onCorrect.forEach((action) => {
            if (action.type === 'openDoor' && typeof action.params.doorId === 'string') {
              openDoor(action.params.doorId);
            }
            if (action.type === 'showMessage' && typeof action.params.message === 'string') {
              setTimeout(() => showMessage(action.params.message), 2000);
            }
            if (action.type === 'addToInventory') {
              const { itemId, itemName, itemDescription, itemIcon } = action.params as {
                itemId: string;
                itemName: string;
                itemDescription: string;
                itemIcon: string;
              };
              addToInventory({
                id: itemId,
                name: itemName,
                description: itemDescription,
                iconId: itemIcon,
                usableInWorld: 'both',
              });
            }
            if (action.type === 'hideObject' && typeof action.params.objectId === 'string') {
              hideObject(action.params.objectId);
            }
            if (action.type === 'removeFromInventory' && typeof action.params.itemId === 'string') {
              removeFromInventory(action.params.itemId);
            }
            if (action.type === 'solveRiddle' && typeof action.params.riddleId === 'string') {
              // Add another riddle to the solved set
              const updatedSolvedRiddles = new Set(get().solvedRiddles);
              updatedSolvedRiddles.add(action.params.riddleId);
              set({ solvedRiddles: updatedSolvedRiddles });
            }
            if (action.type === 'dismountBike') {
              get().dismountBike();
            }
            if (action.type === 'mountBike') {
              const { color } = action.params as { color: 'blue' | 'yellow' | 'red' };
              get().mountBike(color);
            }
            if (action.type === 'switchWorld') {
              // Switch to the specified world (or toggle if no world specified)
              const { world } = action.params as { world?: 'real' | 'spectral' };
              if (world) {
                set({ currentWorld: world });
              } else {
                get().switchWorld();
              }
            }
          });
        }

        // Complete quest objective if linked to this NPC
        interactWithObject(currentRiddle.npcId);

        // Auto-save
        setTimeout(() => get().saveProgress(), 100);

        return true;
      } else {
        // Wrong answer
        showMessage(currentRiddle.incorrectResponse);

        // Close riddle if can't retry
        if (!currentRiddle.canRetry) {
          set({ currentRiddle: null });
        }

        return false;
      }
    },

    closeRiddle: () => {
      set({ currentRiddle: null });
    },

    isRiddleSolved: (riddleId) => {
      return get().solvedRiddles.has(riddleId);
    },

    solveRiddle: (riddleId) => {
      const { currentQuest, completeObjective, showMessage } = get();
      const newSolvedRiddles = new Set(get().solvedRiddles);
      newSolvedRiddles.add(riddleId);
      set({ solvedRiddles: newSolvedRiddles });
      console.log(`✅ Riddle solved: ${riddleId}`);

      // Check if any objectives should be completed when this riddle is solved
      if (currentQuest) {
        // Check main objectives
        const mainObjective = currentQuest.objectives.find(
          (obj) => obj.completeOnRiddleSolved === riddleId && !obj.completed
        );
        if (mainObjective) {
          completeObjective(mainObjective.id);
          showMessage(`✓ ${mainObjective.description}`);
        }

        // Check sub-quest objectives
        if (currentQuest.subQuests) {
          for (const subQuest of currentQuest.subQuests) {
            const subObjective = subQuest.objectives.find(
              (obj) => obj.completeOnRiddleSolved === riddleId && !obj.completed
            );
            if (subObjective) {
              completeObjective(subObjective.id);
              showMessage(`🐾 ${subObjective.description}`);
            }
          }
        }
      }

      // Auto-save
      setTimeout(() => get().saveProgress(), 100);
    },

    // Debug actions
    toggleDebugMode: () => {
      const newDebugMode = !get().debugMode;
      set({ debugMode: newDebugMode });
      console.log(`Debug mode ${newDebugMode ? 'enabled' : 'disabled'}`);
    },

    // Camera/Zoom actions
    setZoom: (zoom) => {
      // Clamp zoom between 20 and 120
      const clampedZoom = Math.max(20, Math.min(120, zoom));
      set({ cameraZoom: clampedZoom });
    },
    zoomIn: () => {
      const currentZoom = get().cameraZoom;
      get().setZoom(currentZoom + 10);
    },
    zoomOut: () => {
      const currentZoom = get().cameraZoom;
      get().setZoom(currentZoom - 10);
    },

    // UI Preferences actions
    toggleJoystick: () => {
      set((state) => ({ showJoystick: !state.showJoystick }));
    },
    toggleZoomControls: () => {
      set((state) => ({ showZoomControls: !state.showZoomControls }));
    },
  }))
);

// ============================================
// SELECTORS
// ============================================

export const selectCurrentWorld = (state: GameStoreState) => state.currentWorld;
export const selectCurrentRoom = (state: GameStoreState) => state.currentRoom;
export const selectPlayer = (state: GameStoreState) => state.player;
export const selectInput = (state: GameStoreState) => state.input;
export const selectIsTransitioning = (state: GameStoreState) =>
  state.isTransitioning;
